import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "contexts/i18nContext";
import React, { ChangeEvent, useRef, useState } from "react";
import {
  addCourtAppeal,
  editComplaint,
} from "services/prosecutorDecipilineService";
import { Complaints, CourtAppeal } from "types";
import { LoadingIcon } from "utils/constant";
import { COURT_APPEAL_STATUS } from "utils/constantValues";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  onComplaintRefresh: any;
}

export default function AppealToCourtModal({
  isOpen,
  setIsOpen,
  complaintData,
  onComplaintRefresh,
}: ModalProps) {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<CourtAppeal>({
    court_hearing_date: "",
    court_number: "",
    court_name: "",
    hearing_court: "",
    court_order_summon: "",
    registration_number: "",
    registration_date: "",
    status: COURT_APPEAL_STATUS.FORWARDED_TO_COORDINATOR,
    files: [] as File[],
  });

  // useEffect(() => {
  //   if (complaintData) {
  //     setFormState((prev) => ({
  //       ...prev,
  //       accused_prosecutor_name: complaintData?.accused_prosecutor_name,
  //       admin_unit_id: complaintData?.admin_unit_id,
  //       org_id: complaintData?.org_id,
  //       user_id_accused_prosecutor_name:
  //         complaintData?.user_id_accused_prosecutor_name,
  //     }));
  //   }
  // }, [complaintData]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!complaintData?.uu_id) {
      showToast(t("complaintNotFound"), "error");
      return;
    }

    setLoading(true);
    const data = new FormData();
    data.append("court_summon_files", (formState?.files ?? [])[0] || "");
    data.append("hearing_date", formState?.court_hearing_date ?? "");
    data.append("court_number", formState?.court_number ?? "");
    data.append("court_name", formState?.court_name ?? "");
    data.append("hearing_court", formState?.hearing_court ?? "");
    data.append("registration_number", formState?.registration_number ?? "");
    data.append("registration_date", formState?.registration_date ?? "");
    data.append("court_order_summon", formState?.court_order_summon ?? "");
    data.append("status", formState.status ?? "");
    data.append("discipline_complain", String(complaintData?.uu_id ?? ""));
    data.append(
      "accused_prosecutor_name",
      String(complaintData?.accused_prosecutor_name ?? ""),
    );
    data.append("admin_unit_id", String(complaintData?.admin_unit_id ?? ""));
    data.append("org_id", String(complaintData?.org_id ?? ""));
    data.append(
      "user_id_accused_prosecutor_name",
      String(complaintData?.user_id_accused_prosecutor_name ?? ""),
    );

    const isFormData = true;

    try {
      const resData = await addCourtAppeal(data, isFormData);

      if (resData) {
        await editComplaint({
          ...complaintData,
          has_courtappeal: true,
        });
      }

      onComplaintRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        court_hearing_date: "",
        court_number: "",
        court_name: "",
        hearing_court: "",
        court_order_summon: "",
        registration_number: "",
        registration_date: "",
        status: COURT_APPEAL_STATUS.FORWARDED_TO_COORDINATOR,
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("appealToCourt")}>
      <form onSubmit={handleSubmit} className="victim-form">
        <section className="victim-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("hearingDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="court_hearing_date"
              placeholder={t("datePlaceholder")}
              value={formState.court_hearing_date ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("courtName")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="text"
              name="court_name"
              value={formState.court_name}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("courtNumber")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="text"
              name="court_number"
              value={formState.court_number}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("registrationNumber")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="text"
              name="registration_number"
              value={formState.registration_number}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("registrationDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="registration_date"
              placeholder={t("datePlaceholder")}
              value={formState.registration_date ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("hearingCourt")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="text"
              name="hearing_court"
              value={formState.hearing_court}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("courtOrderSummon")}
            </label>
            <textarea
              required
              rows={3}
              name="court_order_summon"
              value={formState.court_order_summon}
              onChange={handleChange}
              placeholder={t("detailsPlaceholder")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("courtSummonFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.files && formState?.files?.length > 0 && (
              <div className="file-list">
                {formState?.files?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("submit")
          )}
        </button>
      </form>
    </SideModal>
  );
}
